<template>
  <component :is="tag" :id="id" class="bim-col" :class="[columns, offsets, orders]">
    <!-- @slot Безымянный слот. Используется для добавление сюда содержимого -->
    <slot></slot>
  </component>
</template>

<script>
export default {
  name: 'bimCol',
  props: {
    /**
     * Какой тег будет у контейнера
     */
    tag: {
      type: String,
      default: 'div'
    },
    /**
     * Добавляет контейнеру id
     */
    id: String,
    /**
     * Задает ширину столбца. Работает от 320px и выше
     * @values 1-12, auto
     */
    cols: {
      type: [String, Number]
    },
    /**
     * Изменяет ширину столбца на брейкпоинте от 576px и выше
     * @values 1-12, auto
     */
    sm: {
      type: [String, Number]
    },
    /**
     * Изменяет ширину столбца на брейкпоинте от 768px и выше
     * @values 1-12, auto
     */
    md: {
      type: [String, Number]
    },
    /**
     * Изменяет ширину столбца на брейкпоинте от 960px и выше
     * @values 1-12, auto
     */
    lg: {
      type: [String, Number]
    },
    /**
     * Изменяет ширину столбца на брейкпоинте от 1280px и выше
     * @values 1-12, auto
     */
    xl: {
      type: [String, Number]
    },
    /**
     * Изменяет ширину столбца на брейкпоинте от 1440px и выше
     * @values 1-12, auto
     */
    xxl: {
      type: [String, Number]
    },
    /**
     * Задает отступ слева у колонки
     * @values 1-11
     */
    offset: {
      type: [String, Number]
    },
    /**
     * Изменяет отступ слева у колонки на брейкпоинте от 576px и выше
     * @values 1-11
     */
    offsetSm: {
      type: [String, Number]
    },
    /**
     * Изменяет отступ слева у колонки на брейкпоинте от 768px и выше
     * @values 1-11
     */
    offsetMd: {
      type: [String, Number]
    },
    /**
     * Изменяет отступ слева у колонки на брейкпоинте от 960px и выше
     * @values 1-11
     */
    offsetLg: {
      type: [String, Number]
    },
    /**
     * Изменяет отступ слева у колонки на брейкпоинте от 1280px и выше
     * @values 1-11
     */
    offsetXl: {
      type: [String, Number]
    },
    /**
     * Изменяет отступ слева у колонки на брейкпоинте от 1440px и выше
     * @values 1-11
     */
    offsetXxl: {
      type: [String, Number]
    },
    /**
     * Устанавливает порядок по умолчанию для столбца
     * @values 1-12
     */
    order: {
      type: [String, Number]
    },
    /**
     * Изменяет порядок компонента на брейкпоинте от 576px и выше
     * @values 1-12
     */
    orderSm: {
      type: [String, Number]
    },
    /**
     * Изменяет порядок компонента на брейкпоинте от 760px и выше
     * @values 1-12
     */
    orderMd: {
      type: [String, Number]
    },
    /**
     * Изменяет порядок компонента на брейкпоинте от 960px и выше
     * @values 1-12
     */
    orderLg: {
      type: [String, Number]
    },
    /**
     * Изменяет порядок компонента на брейкпоинте от 1280px и выше
     * @values 1-12
     */
    orderXl: {
      type: [String, Number]
    },
    /**
     * Изменяет порядок компонента на брейкпоинте от 1440px и выше
     * @values 1-12
     */
    orderXxl: {
      type: [String, Number]
    },
  },
  computed: {
    /**
     * Вычисляет какие классы применить в зависимости от заполненных пропов
     */
    columns () {
      let cols, sm, md, lg, xl, xxl

      if (this.cols) cols = this.checkColumns('cols')
      if (this.sm) sm = this.checkColumns('sm')
      if (this.md) md = this.checkColumns('md')
      if (this.lg) lg = this.checkColumns('lg')
      if (this.xl) xl = this.checkColumns('xl')
      if (this.xxl) xxl = this.checkColumns('xxl')

      return [cols, sm, md, lg, xl, xxl]
    },

    offsets () {
      let offset, offsetSm, offsetMd, offsetLg, offsetXl, offsetXxl

      if (this.offset) offset = this.checkOffset('offset')
      if (this.offsetSm) offsetSm = this.checkOffset('offsetSm')
      if (this.offsetMd) offsetMd = this.checkOffset('offsetMd')
      if (this.offsetLg) offsetLg = this.checkOffset('offsetLg')
      if (this.offsetXl) offsetXl = this.checkOffset('offsetXl')
      if (this.offsetXxl) offsetXxl = this.checkOffset('offsetXxl')

      return [offset, offsetSm, offsetMd, offsetLg, offsetXl, offsetXxl]
    },

    orders () {
      let order, orderSm, orderMd, orderLg, orderXl, orderXxl

      if (this.order) order = this.checkOrder('order')
      if (this.orderSm) orderSm = this.checkOrder('orderSm')
      if (this.orderMd) orderMd = this.checkOrder('orderMd')
      if (this.orderLg) orderLg = this.checkOrder('orderLg')
      if (this.orderXl) orderXl = this.checkOrder('orderXl')
      if (this.orderXxl) orderXxl = this.checkOrder('orderXxl')

      return [order, orderSm, orderMd, orderLg, orderXl, orderXxl]
    }
  },
  methods: {
    /**
     * Универсальная функция для вычисления какой класс нужно вернуть в зависимости от переданного параметра
     * @param {string} val - название переменной-пропа для align.
     */
    checkColumns (val) {
      const prefix = val !== 'cols' ? `-${val}-` : '-'
      let result
      for (let i = 1; i <= 12; i++) {
        switch (this[val]) {
          case i:
            result = `col${prefix}${i}`
            break
          case i.toString():
            result = `col${prefix}${i}`
            break
          case 'auto':
            result = `col${prefix}auto`
            break
        }
      }
      return result
    },
    checkOffset (val) {
      const cutVal = val.split('offset').join('').toLowerCase()
      const prefix = cutVal ? `-${cutVal}-` : '-'
      let result

      for (let i = 1; i <= 11; i++) {
        switch (+this[val]) {
          case i:
            result = `offset${prefix}${i}`
            break
        }
      }
      return result
    },
    checkOrder (val) {
      const cutVal = val.split('order').join('').toLowerCase()
      const prefix = cutVal ? `-${cutVal}-` : '-'
      let result

      for (let i = 1; i <= 12; i++) {
        switch (+this[val]) {
          case i:
            result = `order${prefix}${i}`
            break
        }
      }
      return result
    },
  },
}
</script>

<style lang="scss" scoped>
  @mixin columns {
    width: 100%;
    @media screen and (min-width: $breakpoint-xs) {
      padding: 0 $col-gap-xs;
    }
    @media screen and (min-width: $breakpoint-sm) {
      padding: 0 $col-gap-sm;
    }
    @media screen and (min-width: $breakpoint-md) {
      padding: 0 $col-gap-md;
    }
    @media screen and (min-width: $breakpoint-lg) {
      padding: 0 $col-gap-lg;
    }
    @media screen and (min-width: $breakpoint-xl) {
      padding: 0 $col-gap-xl;
    }
    @media screen and (min-width: $breakpoint-xxl) {
      padding: 0 $col-gap-xxl;
    }
  }

  %columns {
    @include columns;
  }

  .bim-col {
    box-sizing: border-box;
    flex-basis: 0;
    flex-grow: 1;
    max-width: 100%;
    @extend %columns;
  }

  $widths:
    '1' 8.333333333333333%,
    '2' 16.66666666666667%,
    '3' 25%,
    '4' 33.33333333333333%,
    '5' 41.66666666666667%,
    '6' 50%,
    '7' 58.33333333333333%,
    '8' 66.66666666666667%,
    '9' 75%,
    '10' 83.33333333333333%,
    '11' 91.66666666666667%,
    '12' 100%,
    'auto' auto;

  $offsets:
    '1' 8.333333333333333%,
    '2' 16.66666666666667%,
    '3' 25%,
    '4' 33.33333333333333%,
    '5' 41.66666666666667%,
    '6' 50%,
    '7' 58.33333333333333%,
    '8' 66.66666666666667%,
    '9' 75%,
    '10' 83.33333333333333%,
    '11' 91.66666666666667%;

  $orders:
    '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12';

  @media screen and (min-width: $breakpoint-xs) {
    %columns-xs {
      @include columns;
    }
    @each $i in $widths {
      .col-#{nth($i, 1)} {
        @extend %columns-xs;
        width: nth($i, 2);
        flex: 0 0 nth($i, 2);
      }
    }

    @each $i in $offsets {
      .offset-#{nth($i, 1)} {
        margin-left: nth($i, 2);
      }
    }

    @each $i in $orders {
      .order-#{nth($i, 1)} {
        order: #{nth($i, 1)}
      }
    }
  }
  @media screen and (min-width: $breakpoint-sm) {
    %columns-sm {
      @include columns;
    }
    @each $i in $widths {
      .col-sm-#{nth($i, 1)} {
        @extend %columns-sm;
        width: nth($i, 2);
        flex: 0 0 nth($i, 2);
      }
    }

    @each $i in $offsets {
      .offset-sm-#{nth($i, 1)} {
        margin-left: nth($i, 2);
      }
    }

    @each $i in $orders {
      .order-sm-#{nth($i, 1)} {
        order: #{nth($i, 1)}
      }
    }
  }
  @media screen and (min-width: $breakpoint-md) {
    %columns-md {
      @include columns;
    }
    @each $i in $widths {
      .col-md-#{nth($i, 1)} {
        @extend %columns-md;
        width: nth($i, 2);
        flex: 0 0 nth($i, 2);
      }
    }

    @each $i in $offsets {
      .offset-md-#{nth($i, 1)} {
        margin-left: nth($i, 2);
      }
    }

    @each $i in $orders {
      .order-md-#{nth($i, 1)} {
        order: #{nth($i, 1)}
      }
    }
  }
  @media screen and (min-width: $breakpoint-lg) {
    %columns-lg {
      @include columns;
    }
    @each $i in $widths {
      .col-lg-#{nth($i, 1)} {
        @extend %columns-lg;
        width: nth($i, 2);
        flex: 0 0 nth($i, 2);
      }
    }

    @each $i in $offsets {
      .offset-lg-#{nth($i, 1)} {
        margin-left: nth($i, 2);
      }
    }

    @each $i in $orders {
      .order-lg-#{nth($i, 1)} {
        order: #{nth($i, 1)}
      }
    }
  }
  @media screen and (min-width: $breakpoint-xl) {
    %columns-xl {
      @include columns;
    }
    @each $i in $widths {
      .col-xl-#{nth($i, 1)} {
        @extend %columns-xl;
        width: nth($i, 2);
        flex: 0 0 nth($i, 2);
      }
    }

    @each $i in $offsets {
      .offset-xl-#{nth($i, 1)} {
        margin-left: nth($i, 2);
      }
    }

    @each $i in $orders {
      .order-xl-#{nth($i, 1)} {
        order: #{nth($i, 1)}
      }
    }
  }
  @media screen and (min-width: $breakpoint-xxl) {
    %columns-xxl {
      @include columns;
    }
    @each $i in $widths {
      .col-xxl-#{nth($i, 1)} {
        @extend %columns-xxl;
        width: nth($i, 2);
        flex: 0 0 nth($i, 2);
      }
    }

    @each $i in $offsets {
      .offset-xxl-#{nth($i, 1)} {
        margin-left: nth($i, 2);
      }
    }

    @each $i in $orders {
      .order-xxl-#{nth($i, 1)} {
        order: #{nth($i, 1)}
      }
    }
  }
</style>
